import {
  faBan,
  faBolt,
  faPause,
  faPen,
  faPlay,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cadenceLabel } from "@recurrences/cadence";
import type { Recurrence } from "@recurrences/recurrencesClient";
import {
  useEndRecurrence,
  usePauseRecurrence,
  useResumeRecurrence,
  useRunRecurrence,
} from "@recurrences/recurrencesQueries";
import {
  Button,
  ConfirmDialog,
  FieldError,
  PaperPanel,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { useState } from "react";

const hasEnded = (recurrence: Recurrence): boolean =>
  recurrence.endsAt != null && new Date(recurrence.endsAt) <= new Date();

/**
 * Where the schedule stands, said in a word under the highlighter that means
 * it: green while it keeps running, yellow while it is parked and waiting on
 * someone, red once it has stopped for good.
 */
const StatusSwipe = ({ recurrence }: { recurrence: Recurrence }) => {
  const [label, tone]: [string, SwipeTone] = hasEnded(recurrence)
    ? ["Ended", "stopped"]
    : recurrence.pausedAt
      ? ["Paused", "flag"]
      : ["Active", "working"];

  return (
    <Swipe tone={tone} data-testid={`recurrence-status-${recurrence.id}`}>
      {label}
    </Swipe>
  );
};

/** How the last run went. Nothing to say before the first one. */
const LastRunSwipe = ({ recurrence }: { recurrence: Recurrence }) => {
  const status = recurrence.lastRunStatus;
  if (!status) return null;
  const failed = status === "failure";
  return (
    <Swipe
      tone={failed ? "stopped" : "working"}
      data-testid={`recurrence-last-run-status-${recurrence.id}`}
    >
      {failed ? "Failed" : "Succeeded"}
    </Swipe>
  );
};

export const RecurrenceCard = ({
  recurrence,
  onEdit,
}: {
  recurrence: Recurrence;
  onEdit: (recurrence: Recurrence) => void;
}) => {
  const pause = usePauseRecurrence();
  const resume = useResumeRecurrence();
  const end = useEndRecurrence();
  const run = useRunRecurrence();
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  const ended = hasEnded(recurrence);
  const paused = recurrence.pausedAt != null;
  const toggling = pause.isPending || resume.isPending;

  return (
    <PaperPanel
      className="p-4"
      data-testid={`recurrence-card-${recurrence.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Titles are free text, so the heading takes the slack and the status
            word keeps its place at the end of the line. */}
        <p className="min-w-0 flex-1 font-display font-medium text-ink">
          {recurrence.title}
        </p>
        <span className="shrink-0 text-xs">
          <StatusSwipe recurrence={recurrence} />
        </span>
      </div>

      <p className="mt-1 text-sm text-ink-body">{cadenceLabel(recurrence)}</p>
      {!ended && (
        <time className="mt-1 block font-mono text-xs text-annotation">
          Next run {formatDateTime(recurrence.nextDueAt)}
        </time>
      )}

      {recurrence.lastRunAt && (
        <div className="mt-1 flex items-center gap-2 text-xs text-annotation">
          <time className="font-mono">
            Last run {formatDateTime(recurrence.lastRunAt)}
          </time>
          <LastRunSwipe recurrence={recurrence} />
        </div>
      )}

      {!ended && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={run.isPending}
            onClick={() => run.mutate(recurrence.id)}
            data-testid={`recurrence-run-${recurrence.id}`}
          >
            <FontAwesomeIcon icon={faBolt} aria-hidden="true" />
            Run now
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={toggling}
            onClick={() =>
              paused
                ? resume.mutate(recurrence.id)
                : pause.mutate(recurrence.id)
            }
            data-testid={`recurrence-toggle-${recurrence.id}`}
          >
            <FontAwesomeIcon
              icon={paused ? faPlay : faPause}
              aria-hidden="true"
            />
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(recurrence)}
            data-testid={`recurrence-edit-${recurrence.id}`}
          >
            <FontAwesomeIcon icon={faPen} aria-hidden="true" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmingEnd(true)}
            data-testid={`recurrence-end-${recurrence.id}`}
            /* Ending is the one irreversible action on the card, so it reads in
               stopped ink instead of taking a louder surface than the rest. */
            className="text-ink-stopped"
          >
            <FontAwesomeIcon icon={faBan} aria-hidden="true" />
            End
          </Button>
        </div>
      )}

      {!ended && (pause.isError || resume.isError) && (
        <FieldError className="mt-2">
          Couldn’t {paused ? "resume" : "pause"} this recurrence. Try again.
        </FieldError>
      )}

      {!ended && run.isError && (
        <FieldError className="mt-2">
          Couldn’t run this recurrence. Try again.
        </FieldError>
      )}

      <ConfirmDialog
        open={confirmingEnd}
        // Conditional: the sheet stays mounted while closed, so an
        // unconditional handle would report an open dialog that isn't there.
        data-testid={
          confirmingEnd ? `recurrence-end-confirm-${recurrence.id}` : undefined
        }
        title="End this recurrence?"
        confirmLabel="End"
        destructive
        busy={end.isPending}
        onCancel={() => {
          end.reset();
          setConfirmingEnd(false);
        }}
        onConfirm={() =>
          end.mutate(recurrence.id, {
            onSuccess: () => setConfirmingEnd(false),
          })
        }
        confirmTestId={`recurrence-end-confirm-${recurrence.id}-confirm`}
        cancelTestId={`recurrence-end-confirm-${recurrence.id}-cancel`}
      >
        <p className="text-sm text-ink-body">
          Ending{" "}
          <strong className="font-medium text-ink">{recurrence.title}</strong>{" "}
          stops it from running again. This can&apos;t be undone.
        </p>
        {end.isError && (
          <FieldError className="mt-2">
            Couldn’t end this recurrence. Try again.
          </FieldError>
        )}
      </ConfirmDialog>
    </PaperPanel>
  );
};
